import React from 'react'

import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'

import { BarPlot } from '@mui/x-charts/BarChart'
import { ChartContainer } from '@mui/x-charts/ChartContainer'
import { ChartsXAxis } from '@mui/x-charts/ChartsXAxis'

import { useContainerDimensions } from '../../hooks/useContainerDimensions'
import type { Legends } from '../../../core'
import type { DataSet, LegendProps } from '../../../core/types/InsightsInstanceSlotData'

import { CustomDoubleBarPlotMXUI } from './CustomDoubleBar'
import { Legend } from './ChartLegend'
import { EmptyBarChart } from './EmptyBarChart'

interface DoubleBarChartProps {
  // Shaped comparison series (one entry per month column), built by the caller (buildComparisonSeries)
  // \u2014 mirroring how BarchartBlock builds `data` for BarChartV2.
  bars: DataSet[]
  // Two-entry palette [main, comparison]. Resolved by the caller (the card via getLayoutConfig). This
  // prop is the only color hook \u2014 getLayoutConfig itself is never modified.
  colors: string[]
  // Same shape as instance_slot_data.legends; mapped onto the palette below.
  legends?: (Legends | LegendProps)[]
  isMini?: boolean
  // Called on a bar click with the clicked column and its sub-series (0 = main, 1 = comparison); the
  // caller decides what a click does (card opens the drawer, drill-down updates its selection).
  onBarClick: (columnIndex: number, subIndex: number) => void
}

/**
 * Pure double-bar chart (month columns x main/comparison). Presentational and self-contained \u2014 it
 * receives shaped data + palette as props and reports clicks via onBarClick, holding no store,
 * selection, or data-shaping of its own. This mirrors BarChartV2; the DoubleBarChartBlock card
 * wrapper builds the data and injects the store behavior, exactly as BarchartBlock wraps BarChartV2.
 */
export const DoubleBarChart = ({
  bars,
  colors,
  legends,
  isMini = false,
  onBarClick,
}: DoubleBarChartProps) => {
  // The most recent (last) column is emphasized; the others are dimmed.
  const selectedTabIndex = bars.length - 1
  const barGapRatio = 0.005
  const categoryGapRatio = 0.16

  const containerRef = React.useRef<HTMLDivElement>(null)
  const { height, width } = useContainerDimensions(containerRef)

  const handleBarClick = ({ dataIndex, seriesId }: { dataIndex: number; seriesId: string }) => {
    const seriesType = Number(seriesId.split('-').pop())
    onBarClick(dataIndex, seriesType)
  }

  // colors can be empty for an unregistered variant (getLayoutConfig miss); fall back to transparent
  // so a swatch never renders as default-black.
  const legendItems = legends?.map((legend, index) => ({
    color: colors[index] ?? 'transparent',
    ...legend,
  })) as LegendProps[]

  return bars.length > 0 ? (
    <Stack sx={{ mb: 0, width: '100%', p: 0 }}>
      <Box
        ref={containerRef}
        sx={{
          height: isMini ? 120 : 172,
          touchAction: 'pan-y',
          width: '100%',
          p: 0,
          m: 0,
          '& svg': {
            touchAction: 'pan-y',
            userSelect: 'none',
          },
        }}
      >
        <ChartContainer
          colors={colors}
          dataset={bars as any[]}
          height={height}
          margin={{
            bottom: 16,
            left: 24,
            right: 24,
            top: 0,
          }}
          series={[
            { dataKey: 'main', type: 'bar' },
            { dataKey: 'comparison', type: 'bar' },
          ]}
          width={width}
          xAxis={[
            {
              barGapRatio,
              categoryGapRatio,
              dataKey: 'label',
              scaleType: 'band',
            },
          ]}
          yAxis={[{ width: 0 }]}
        >
          <ChartsXAxis
            disableTicks={false}
            slots={{
              axisTickLabel: ({ text, ...restProps }) => {
                const selectedLabel = bars[selectedTabIndex]?.label
                return (
                  <text
                    {...restProps}
                    style={
                      {
                        ...restProps.style,
                        fontWeight: selectedLabel === text ? 700 : 400,
                      } as React.CSSProperties
                    }
                  >
                    {text}
                  </text>
                )
              },
            }}
            sx={{
              '.MuiChartsAxis-line': {
                stroke: 'border.darker',
              },
            }}
            tickLabelStyle={{
              fontSize: 11,
              fill: 'text.secondary',
              fontWeight: 400,
            }}
          />
          <BarPlot
            onItemClick={(_, { dataIndex, seriesId }) =>
              handleBarClick({ dataIndex, seriesId: seriesId.toString() })
            }
            slots={{
              bar: (props) => {
                const { ownerState, ...defaultProps } = props
                const isSelected = selectedTabIndex === ownerState.dataIndex
                const barColor = isSelected ? ownerState.color : `${ownerState.color}50`

                return (
                  <CustomDoubleBarPlotMXUI
                    {...defaultProps}
                    color={barColor}
                    data={bars}
                    series={{
                      index: ownerState.dataIndex,
                      type: Number(ownerState.id?.toString().split('-').pop() ?? 0),
                    }}
                    style={{
                      height: props.height,
                      x: props.x,
                      y: props.y,
                      width: props.width,
                    }}
                  />
                )
              },
            }}
          />
        </ChartContainer>
      </Box>

      {legendItems && !isMini && legendItems.length > 0 && (
        <Legend legendItems={legendItems} legendStyle={{ ml: 24 }} />
      )}
    </Stack>
  ) : (
    <EmptyBarChart />
  )
}
